import React, { useState } from 'react';
import { snoozeSurvey, markSurveyShown } from '../utils/surveyTrigger';
import { API_BASE } from '../config';

// Design Guru section list — kept in sync with OM_SECTIONS in
// OperatingModelCanvas.js. If the canvas list changes, update here too.
const DESIGN_SECTIONS = [
  'People, Roles & Structure',
  'Culture & Leadership',
  'Location & Infrastructure',
  'Suppliers & Partners',
  'Customer Interactions',
  'Process & Ways of Work',
  'Governance & Review Rhythm',
  'Finance & Commercial',
  'IT & Data Security',
  'Product & Service Portfolio',
  'Business Development',
  'Marketing & Brand',
];

const SECTIONS = [
  {
    title: 'First impressions',
    questions: [
      {
        id: 'q1',
        text: "Before today, had you ever stepped back to think through how your business is designed in a structured way?",
        type: 'single',
        options: ['Yes', 'Partially', 'No'],
      },
      {
        id: 'q2',
        text: 'How did GuruOne compare to what you expected?',
        type: 'single',
        options: ['Much better', 'Better', 'About right', 'Worse', 'Much worse'],
      },
    ],
  },
  {
    title: 'The conversation experience',
    questions: [
      {
        id: 'q3',
        text: 'When you used the deep dive chat, did it surface things you hadn\'t already considered?',
        type: 'single',
        options: ['Yes, several', 'One or two', 'Not really', 'I didn\'t use the chat'],
      },
      {
        id: 'q4',
        text: 'Did GuruOne ask the right questions, or was something important missing?',
        type: 'text',
      },
      {
        id: 'q5',
        text: 'Was there any point where the conversation felt frustrating or unhelpful?',
        type: 'text',
      },
    ],
  },
  {
    title: 'Design Guru canvas',
    questions: [
      {
        id: 'q6',
        text: 'Which Design Guru sections felt most valuable?',
        type: 'multi',
        options: DESIGN_SECTIONS,
      },
      {
        id: 'q7',
        text: 'Which Design Guru sections felt least relevant or hardest to engage with?',
        type: 'multi',
        options: DESIGN_SECTIONS,
      },
      {
        id: 'q8',
        text: 'Did anything feel confusing or unclear?',
        type: 'text',
      },
    ],
  },
  {
    title: 'Usefulness',
    questions: [
      {
        id: 'q9',
        text: 'Did completing sections change how you\'re thinking about your business?',
        type: 'single',
        options: ['Yes, significantly', 'Somewhat', 'Not really'],
      },
      {
        id: 'q10',
        text: 'Is there a conversation or question type that GuruOne should have but doesn\'t?',
        type: 'text',
      },
    ],
  },
  {
    title: 'Recommend & reflect',
    questions: [
      {
        id: 'q11',
        text: 'How likely are you to recommend GuruOne to another founder?',
        type: 'nps',
      },
      {
        id: 'q12',
        text: 'What\'s the single most useful thing GuruOne did for you today?',
        type: 'text',
      },
      {
        id: 'q13',
        text: 'What\'s the one thing you\'d change?',
        type: 'text',
      },
    ],
  },
];

export default function SurveyModal({ onClose, userId }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const totalSections = SECTIONS.length;
  const section = SECTIONS[currentSection];

  function handleSingleAnswer(qId, value) {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  }

  function handleMultiAnswer(qId, value) {
    setAnswers(prev => {
      const current = prev[qId] || [];
      return {
        ...prev,
        [qId]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value],
      };
    });
  }

  function handleTextAnswer(qId, value) {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  }

  function handleDismiss() {
    snoozeSurvey();
    onClose();
  }

  function canAdvance() {
    // Only single-select and NPS are required to advance.
    return section.questions.every(q => {
      if (q.type === 'single' || q.type === 'nps') return answers[q.id] !== undefined;
      return true;
    });
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await fetch(`${API_BASE}/survey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, answers }),
      });
      markSurveyShown();
      setSubmitted(true);
    } catch (err) {
      console.error('Survey submission failed', err);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.thankyou}>
            <h2 style={styles.thankyouTitle}>Thank you</h2>
            <p style={styles.thankyouText}>
              Your feedback goes directly to the team building GuruOne. It genuinely shapes what we build next.
            </p>
            <button style={styles.primaryBtn} onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <p style={styles.eyebrow}>Got 3 minutes?</p>
            <h2 style={styles.title}>{section.title}</h2>
          </div>
          <button style={styles.dismissBtn} onClick={handleDismiss} aria-label="Remind me later">
            Remind me later
          </button>
        </div>

        <div style={styles.progress}>
          {SECTIONS.map((_, i) => (
            <div
              key={i}
              style={{
                ...styles.progressDot,
                backgroundColor: i <= currentSection ? '#2563EB' : '#E5E7EB',
              }}
            />
          ))}
        </div>

        <div style={styles.body}>
          {section.questions.map(q => (
            <div key={q.id} style={styles.question}>
              <p style={styles.questionText}>{q.text}</p>

              {q.type === 'single' && (
                <div style={styles.options}>
                  {q.options.map(opt => (
                    <button
                      key={opt}
                      style={{
                        ...styles.optionBtn,
                        ...(answers[q.id] === opt ? styles.optionBtnSelected : {}),
                      }}
                      onClick={() => handleSingleAnswer(q.id, opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {q.type === 'multi' && (
                <div style={styles.options}>
                  {q.options.map(opt => (
                    <button
                      key={opt}
                      style={{
                        ...styles.optionBtn,
                        ...((answers[q.id] || []).includes(opt) ? styles.optionBtnSelected : {}),
                      }}
                      onClick={() => handleMultiAnswer(q.id, opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {q.type === 'text' && (
                <textarea
                  style={styles.textarea}
                  rows={3}
                  placeholder="Optional — share as much or as little as you like"
                  value={answers[q.id] || ''}
                  onChange={e => handleTextAnswer(q.id, e.target.value)}
                />
              )}

              {q.type === 'nps' && (
                <div style={styles.npsRow}>
                  {Array.from({ length: 11 }, (_, i) => (
                    <button
                      key={i}
                      style={{
                        ...styles.npsBtn,
                        ...(answers[q.id] === i ? styles.npsBtnSelected : {}),
                      }}
                      onClick={() => handleSingleAnswer(q.id, i)}
                    >
                      {i}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={styles.footer}>
          {currentSection > 0 && (
            <button style={styles.backBtn} onClick={() => setCurrentSection(s => s - 1)}>
              Back
            </button>
          )}
          {currentSection < totalSections - 1 ? (
            <button
              style={{ ...styles.primaryBtn, ...(canAdvance() ? {} : styles.primaryBtnDisabled) }}
              disabled={!canAdvance()}
              onClick={() => setCurrentSection(s => s + 1)}
            >
              Next
            </button>
          ) : (
            <button
              style={{ ...styles.primaryBtn, ...(canAdvance() ? {} : styles.primaryBtnDisabled) }}
              disabled={!canAdvance() || submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Sending…' : 'Submit feedback'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '16px',
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '560px',
    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '24px 24px 0',
  },
  eyebrow: { fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 4px' },
  title: { fontSize: '20px', fontWeight: 600, color: '#111827', margin: 0 },
  dismissBtn: {
    background: 'none', border: 'none', color: '#9CA3AF', fontSize: '13px',
    cursor: 'pointer', padding: '4px 0', whiteSpace: 'nowrap', flexShrink: 0,
  },
  progress: { display: 'flex', gap: '6px', padding: '16px 24px 0' },
  progressDot: { height: '4px', flex: 1, borderRadius: '2px', transition: 'background-color 0.2s' },
  body: { padding: '20px 24px', flex: 1 },
  question: { marginBottom: '24px' },
  questionText: { fontSize: '15px', fontWeight: 500, color: '#111827', marginBottom: '12px' },
  options: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  optionBtn: {
    padding: '8px 14px', borderRadius: '6px', border: '1.5px solid #E5E7EB',
    background: '#fff', fontSize: '14px', color: '#374151', cursor: 'pointer',
    transition: 'all 0.15s',
  },
  optionBtnSelected: { borderColor: '#2563EB', background: '#EFF6FF', color: '#1D4ED8', fontWeight: 500 },
  textarea: {
    width: '100%', border: '1.5px solid #E5E7EB', borderRadius: '6px',
    padding: '10px 12px', fontSize: '14px', color: '#374151', resize: 'vertical',
    fontFamily: 'inherit', boxSizing: 'border-box',
    outline: 'none',
  },
  npsRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  npsBtn: {
    width: '40px', height: '40px', borderRadius: '6px', border: '1.5px solid #E5E7EB',
    background: '#fff', fontSize: '14px', color: '#374151', cursor: 'pointer',
  },
  npsBtnSelected: { borderColor: '#2563EB', background: '#EFF6FF', color: '#1D4ED8', fontWeight: 600 },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: '10px',
    padding: '16px 24px', borderTop: '1px solid #F3F4F6',
  },
  backBtn: {
    padding: '10px 20px', borderRadius: '6px', border: '1.5px solid #E5E7EB',
    background: '#fff', fontSize: '14px', color: '#374151', cursor: 'pointer',
  },
  primaryBtn: {
    padding: '10px 24px', borderRadius: '6px', border: 'none',
    background: '#2563EB', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
  },
  primaryBtnDisabled: { background: '#93C5FD', cursor: 'not-allowed' },
  thankyou: { padding: '48px 32px', textAlign: 'center' },
  thankyouTitle: { fontSize: '22px', fontWeight: 600, color: '#111827', marginBottom: '12px' },
  thankyouText: { fontSize: '15px', color: '#6B7280', marginBottom: '28px', lineHeight: '1.6' },
};
